import React from 'react';
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Box,
    Paper,
    Grid
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import useChallengeStore from '../../store/challengeStore';
import ImageUpload from './ImageUpload';

import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';

// quill-emoji must be imported as default export
import quillEmoji from 'quill-emoji';

// Register quill-emoji modules
Quill.register('modules/emoji-shortname', quillEmoji.ShortNameEmoji);
Quill.register('modules/emoji-toolbar', quillEmoji.ToolbarEmoji);
Quill.register('formats/emoji', quillEmoji.EmojiBlot);

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
        width: '100%',
        margin: 0
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    formControl: {
        marginBottom: theme.spacing(2),
        width: '100%'
    },
    quillEditor: {
        '& .ql-container': {
            minHeight: '200px',
            fontSize: '16px',
            fontFamily: theme.typography.fontFamily
        },
        '& .ql-editor': {
            minHeight: '200px'
        },
        '& .ql-toolbar': {
            backgroundColor: '#f5f5f5',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
        }
    },
    quillLabel: {
        marginBottom: theme.spacing(1),
        fontSize: '12px',
        color: 'rgba(0, 0, 0, 0.54)',
        fontWeight: 400
    }
}));

// Quill toolbar configuration
const quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['emoji'],
        ['clean']
    ],
    'emoji-toolbar': true,
    'emoji-shortname': true
};

const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link',
    'color', 'background',
    'align',
    'emoji'
];

const Page1GeneralInfo = () => {
    const classes = useStyles();

    const {
        challengeTitle,
        challengeDescription,
        challengeLearningTopic,
        learningTopicLevel,
        isValidation,
        isForTeam,
        challengeDifficulty,
        challengeTags,
        averageTime,
        challengeLinkDemopath,
        timerType,
        updateChallengeInfo
    } = useChallengeStore();

    const handleChange = (field) => (event) => {
        updateChallengeInfo(field, event.target.value);
    };

    const handleQuillChange = (value) => {
        updateChallengeInfo('challengeDescription', value);
    };

    return (
        <Paper className={classes.container} elevation={2}>
            <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                General Challenge Information
            </Typography>

            <Box className={classes.section}>
                <Grid container spacing={2}>
                    {/* Row 1: Challenge Title */}
                    <Grid item xs={12}>
                        <TextField
                            label="Challenge Title *"
                            value={challengeTitle}
                            onChange={handleChange('challengeTitle')}
                            fullWidth
                            variant="outlined"
                            placeholder="Enter challenge title"
                        />
                    </Grid>

                    {/* Row 2: Challenge Description */}
                    <Grid item xs={12}>
                        <Typography className={classes.quillLabel}>
                            Challenge Description *
                        </Typography>
                        <Box className={classes.quillEditor}>
                            <ReactQuill
                                theme="snow"
                                value={challengeDescription || ''}
                                onChange={handleQuillChange}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Enter challenge description with formatting..."
                            />
                        </Box>
                    </Grid>

                    {/* Row 3: Challenge Demo Link/Image */}
                    <Grid item xs={12}>
                        <ImageUpload
                            value={challengeLinkDemopath}
                            onChange={handleChange('challengeLinkDemopath')}
                            label="Challenge Demopaths"
                            uploadPath="gams-common/demo-path"
                            acceptedFileTypes="image/*,application/pdf"
                        />
                    </Grid>

                    {/* Row 4: Learning Topic and Timer Configuration */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Learning Topic *"
                            value={challengeLearningTopic}
                            onChange={handleChange('challengeLearningTopic')}
                            fullWidth
                            variant="outlined"
                            placeholder="Learning topic container"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Timer Type *</InputLabel>
                            <Select
                                value={timerType || 'noTimer'}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Store both timerType (for UI) and timerEnabled (boolean for backend)
                                    updateChallengeInfo('timerType', value);
                                    updateChallengeInfo('timerEnabled', value === 'incremental');
                                }}
                                label="Timer Type *"
                            >
                                <MenuItem value="noTimer">No Timer</MenuItem>
                                <MenuItem value="incremental">Incremental Timer</MenuItem>
                                <MenuItem value="countdown">Countdown Timer</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                            Configure timer behavior for all challenge steps
                        </Typography>
                    </Grid>

                    {/* Row 4: Learning Topic Level and Challenge Difficulty */}
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Learning Topic Level *</InputLabel>
                            <Select
                                value={learningTopicLevel}
                                onChange={handleChange('learningTopicLevel')}
                                label="Learning Topic Level *"
                            >
                                <MenuItem value={1}>Beginner</MenuItem>
                                <MenuItem value={2}>Intermediate</MenuItem>
                                <MenuItem value={3}>Expert</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Challenge Difficulty *</InputLabel>
                            <Select
                                value={challengeDifficulty}
                                onChange={handleChange('challengeDifficulty')}
                                label="Challenge Difficulty *"
                            >
                                <MenuItem value="Easy">Easy</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="Advanced">Advanced</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Row 5: Validation Challenge and Team Challenge */}
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Validation Challenge *</InputLabel>
                            <Select
                                value={isValidation}
                                onChange={handleChange('isValidation')}
                                label="Validation Challenge *"
                            >
                                <MenuItem value={false}>No</MenuItem>
                                <MenuItem value={true}>Yes</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Team Challenge *</InputLabel>
                            <Select
                                value={isForTeam}
                                onChange={handleChange('isForTeam')}
                                label="Team Challenge *"
                            >
                                <MenuItem value={false}>No</MenuItem>
                                <MenuItem value={true}>Yes</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Row 6: Challenge Tags and Average Completion Time */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Challenge Tags"
                            value={challengeTags}
                            onChange={handleChange('challengeTags')}
                            fullWidth
                            variant="outlined"
                            placeholder="Excel; Spreadsheets; Advertising Formulas; Brand"
                            helperText="Separate tags with semicolon (;)"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Average Completion Time"
                            value={averageTime}
                            onChange={handleChange('averageTime')}
                            fullWidth
                            variant="outlined"
                            placeholder="e.g. 15"
                            helperText="Estimated time in minutes to complete the challenge"
                        />
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
};

export default Page1GeneralInfo;
